import React from 'react';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { DonutChart } from '@/components/ui/charts/DonutChart';
import type { DonutSegment } from '@/components/ui/charts/DonutChart';
import { colors } from '@/assets/styles/colors';


export interface DonutSummaryRow {
  label: string;
  value: string;
  valueColor?: string;
}

interface DonutSummaryCardProps {
  title: string;
  percentage?: number;
  color?: string;
  segments?: DonutSegment[];
  labelColor?: string;
  percentageLabel: string;
  percentageSubLabel?: string;
  rows: DonutSummaryRow[];
  // Optional slot rendered next to the title (e.g. an `ActionMenu` trigger for "Ver
  // detalle"). Generic by design so every card in the epic can reuse it without adding
  // domain-specific props.
  headerAction?: React.ReactNode;
  'data-testid'?: string;
}

export const DonutSummaryCard: React.FC<DonutSummaryCardProps> = ({
  title,
  percentage,
  color,
  segments,
  labelColor,
  percentageLabel,
  percentageSubLabel,
  rows,
  headerAction,
  'data-testid': dataTestId,
}) => (
  <Card
    elevation={1}
    borderRadius="16px"
    padding="24px"
    style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 280 }}
    data-testid={dataTestId}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <Text
        variant="body-medium"
        fontFamily="montserrat"
        color={colors.grays.neutral66}
        style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}
        data-testid={`${dataTestId}-title`}
      >
        {title}
      </Text>
      {headerAction}
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, flex: 1 }}>
      <DonutChart
        percentage={percentage}
        color={color}
        segments={segments}
        labelColor={labelColor}
        size={195}
        strokeWidth={18}
        label={percentageLabel}
        subLabel={percentageSubLabel}
        data-testid={`${dataTestId}-donut`}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {rows.map((row, index) => (
          <div
            key={index}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            data-testid={`${dataTestId}-row-${index}`}
          >
            <Text
              as="span"
              variant="small-regular"
              fontFamily="montserrat"
              color={colors.grays.neutral66}
              data-testid={`${dataTestId}-row-${index}-label`}
            >
              {row.label}
            </Text>
            <Text
              variant="small-bold"
              fontFamily="montserrat"
              lineHeight="18px"
              color={row.valueColor ?? colors.grays.neutral66}
              data-testid={`${dataTestId}-row-${index}-value`}
            >
              {row.value}
            </Text>
          </div>
        ))}
      </div>
    </div>
  </Card>
);
